const API_URL = process.env.REACT_APP_API_URL;

// Parses a failed response's JSON body (if any) and throws an Error whose
// message is the server's own error string when available, with any extra
// fields (e.g. FinalizeVisit's `blockingMedia`) attached to the Error object
// rather than swallowed — callers upstream (the upload hook, VisitModal)
// need that detail, not just "request failed".
async function throwForErrorResponse(response, fallbackMessage) {
    let body = null;
    try {
        body = await response.json();
    } catch (e) {
        // Body wasn't JSON (or was empty) — fall through with no detail.
    }

    const error = new Error(body?.error || fallbackMessage);
    if (body) {
        Object.keys(body).forEach(key => {
            if (key !== 'error') error[key] = body[key];
        });
    }
    error.status = response.status;
    throw error;
}

const api = {
    // Get all discs for homepage map
    async getDiscs() {
        try {
            const response = await fetch(`${API_URL}/api/discs`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data.discs;
        } catch (error) {
            console.error('Error fetching discs:', error);
            throw error;
        }
    },

    // Get individual disc
    async getDisc(discId) {
        try {
            const response = await fetch(`${API_URL}/api/discs/${discId}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error fetching disc:', error);
            throw error;
        }
    },

    // ── Legacy two-phase flow (SubmitVisit / CompleteVisitUpload) ──────────
    // Deliberately left in place, unused once VisitModal cuts over to the
    // methods below. Not removed until the new flow is verified end-to-end
    // and prod has migrated — see DoZESQ SOP / Recipe deployment guidance.

    async submitVisit(visitData) {
        const response = await fetch(`${API_URL}/api/visits`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(visitData)
        });
        if (!response.ok) {
            throw new Error('Failed to submit visit');
        }
        return response.json();
    },

    async completeVisitUpload(visitId, uploadedMedia) {
        const response = await fetch(`${API_URL}/api/visits/${visitId}/complete`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                visitId,
                uploadedMedia
            })
        });
        if (!response.ok) {
            throw new Error('Failed to complete upload');
        }
        return response.json();
    },

    // ── Draft-first flow (1C/1D/1E), per the DoZESQ Media Upload Recipe ────

    // Fires the moment the visit form opens (Recipe Principle 2).
    // Returns { visitId }.
    async createDraftVisit(discId) {
        try {
            const response = await fetch(`${API_URL}/api/visits/draft`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ discId })
            });
            if (!response.ok) {
                await throwForErrorResponse(response, 'Failed to create draft visit');
            }
            return response.json();
        } catch (error) {
            console.error('Error creating draft visit:', error);
            throw error;
        }
    },

    // Fires the moment a file is added (Recipe Principle 3). contentType
    // must be the FINAL content type — i.e. after photo compression, since
    // the backend derives the S3 key extension and validates it against
    // `type` from this value. fileSize (1E.3) lets the backend compute how
    // many 5MB parts to presign — always multipart now, even a small file
    // just resolves to 1 part.
    // Returns { mediaId, uploadId, key, parts: [{partNumber, uploadUrl}], expiresIn }.
    async addVisitMedia({ visitId, type, visibility, contentType, fileSize }) {
        try {
            const response = await fetch(`${API_URL}/api/visits/media`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visitId, type, visibility, contentType, fileSize })
            });
            if (!response.ok) {
                // 409 here specifically means the draft is gone/expired —
                // callers should treat this as unrecoverable for this file,
                // not something auto-retry (Recipe Principle: 3 attempts)
                // should keep hammering.
                await throwForErrorResponse(response, 'Failed to reserve upload slot');
            }
            return response.json();
        } catch (error) {
            console.error('Error adding visit media:', error);
            throw error;
        }
    },

    // 1E.3: called once every part has PUT successfully, to assemble the
    // multipart upload into the final S3 object. Touches no DynamoDB on the
    // backend — CompleteVisitMedia's existing S3 trigger picks up from the
    // resulting object-created event, same as it always has.
    // parts must be [{partNumber, eTag}], sourced from uploadMultipart()'s
    // return value. Returns { visitId, mediaId, completed }.
    async completeVisitMediaUpload({ visitId, mediaId, key, uploadId, parts }) {
        try {
            const response = await fetch(`${API_URL}/api/visits/media/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visitId, mediaId, key, uploadId, parts })
            });
            if (!response.ok) {
                await throwForErrorResponse(response, 'Failed to complete upload');
            }
            return response.json();
        } catch (error) {
            console.error('Error completing visit media upload:', error);
            throw error;
        }
    },

    // 1E.4: manual-Retry entry point after a failed/stale multipart session
    // (ChatGPT feedback item 6 — presigned URL expiry vs. resumable retry).
    // Checks both the draft's DynamoDB state and the S3 multipart session
    // (via ListParts) server-side, rather than trusting the client's own
    // memory of what's still valid. parts is [{partNumber, uploadUrl}] for
    // EVERY part number (uploadUrl null for already-completed ones) — same
    // shape addVisitMedia returns, so runUploadSession can consume it
    // unchanged whether it's a fresh session or a resumed one.
    // Returns { resumable: false } OR { resumable: true, completedParts, parts }.
    async resumeVisitMedia({ visitId, mediaId, uploadId, key, totalParts }) {
        try {
            const response = await fetch(`${API_URL}/api/visits/media/resume`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visitId, mediaId, uploadId, key, totalParts })
            });
            if (!response.ok) {
                await throwForErrorResponse(response, 'Failed to check upload session');
            }
            return response.json();
        } catch (error) {
            console.error('Error resuming visit media upload:', error);
            throw error;
        }
    },

    // The X button, for both in-flight and completed uploads (Recipe
    // Principle 7). Idempotent — safe to call more than once for the same
    // mediaId. Returns { visitId, mediaId, deleted }.
    async deleteVisitMedia({ visitId, mediaId }) {
        try {
            const response = await fetch(`${API_URL}/api/visits/media`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visitId, mediaId }),
                // Lets this request survive the tab losing foreground/active
                // status — mobile browsers can otherwise silently drop an
                // in-flight fetch on backgrounding, which is what orphaned
                // the placeholder in mobile testing (desktop, same code
                // path, worked correctly). Small JSON body, well under
                // keepalive's ~64KB limit.
                keepalive: true
            });
            if (!response.ok) {
                await throwForErrorResponse(response, 'Failed to remove media');
            }
            return response.json();
        } catch (error) {
            console.error('Error deleting visit media:', error);
            throw error;
        }
    },

    // Submit. Backend re-validates uploadStatus server-side regardless of
    // what the UI already checked — a 400 here means at least one item is
    // still 'uploading' or 'failed'; the thrown Error carries `blockingMedia`
    // (array of { mediaId, uploadStatus }) so the caller can surface exactly
    // which item(s) are the problem rather than a generic failure.
    // Returns { visit }.
    async finalizeVisit(visitId, { location, courseName, thoughts, familyOnly, removedMediaIds }) {
        try {
            const response = await fetch(`${API_URL}/api/visits/${visitId}/finalize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visitId, location, courseName, thoughts, familyOnly, removedMediaIds })
            });
            if (!response.ok) {
                await throwForErrorResponse(response, 'Failed to submit visit');
            }
            return response.json();
        } catch (error) {
            console.error('Error finalizing visit:', error);
            throw error;
        }
    },

    // Recipe Principle 6's confirmation poll. mediaIds is an array, scoped
    // to just the items the caller is still waiting on — pass undefined/[]
    // to get every item's status instead.
    // Returns { visitId, statuses: { [mediaId]: uploadStatus } }. A mediaId
    // missing from `statuses` means it's no longer on the visit at all
    // (e.g. raced with a delete) — callers should treat that the same as
    // "stop waiting on it", not as an error.
    async getVisitMediaStatus(visitId, mediaIds, reconcile = false) {
        try {
            const params = new URLSearchParams();
            if (mediaIds && mediaIds.length > 0) {
                params.set('mediaIds', mediaIds.join(','));
            }
            if (reconcile) {
                params.set('reconcile', 'true');
            }
            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await fetch(`${API_URL}/api/visits/${visitId}/media-status${query}`);
            if (!response.ok) {
                await throwForErrorResponse(response, 'Failed to fetch media status');
            }
            return response.json();
        } catch (error) {
            console.error('Error fetching visit media status:', error);
            throw error;
        }
    },

    // Cancel / back-navigation (Recipe: "Draft Record Lifecycle & Cleanup").
    // Idempotent, same reasoning as deleteVisitMedia — safe to call even if
    // the draft is already gone. Returns { visitId, deleted }.
    async deleteDraftVisit(visitId) {
        try {
            const response = await fetch(`${API_URL}/api/visits/${visitId}/draft`, {
                method: 'DELETE',
                keepalive: true
            });
            if (!response.ok) {
                await throwForErrorResponse(response, 'Failed to delete draft visit');
            }
            return response.json();
        } catch (error) {
            console.error('Error deleting draft visit:', error);
            throw error;
        }
    }
};

export { api };